import type { Coupon, CouponPrintOverlayLayout } from '../types';
import { resolveBuildingWing } from './buildingUnitUtils';
import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';
import {
  cssAspectRatioForCardSize,
  getMmSheetLayoutConfig,
  isMmSheetCardSize,
  type CouponCardSize,
} from './couponCardSizes';
import {
  clampA4Rows,
  COUPON_A4_MM_PAGE_MARGIN_MM,
  couponsPerSheet,
  gridHeightMm,
  gridWidthMm,
  horizontalCutYsMm,
  verticalCutXMm,
} from './couponSheetMm';
import { isPrintOverlayVisible, mergeCouponPrintOverlay, sanitizeCssColor } from './couponPrintOverlay';
import { fetchCouponTemplateImageBlob } from './couponTemplateImageUpload';

function escapeCsv(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function couponsToCSV(coupons: Coupon[], options?: { buildingUnit?: boolean }): string {
  const header = [
    'code',
    'barcode_value',
    'unit_label',
    'building_wing',
    'campaign_id',
    'discount_type',
    'discount_value',
    'usage_limit',
    'used_count',
    'total_discount_given',
    'start_date',
    'end_date',
    'expires_at',
    'status',
  ];
  const buildingUnit = options?.buildingUnit ?? coupons.some((c) => c.isPermanent);
  const rows = coupons.map((coupon) => [
    coupon.code,
    getCouponBarcodeValue(coupon.code, buildingUnit || coupon.isPermanent),
    coupon.unitLabel ?? '',
    resolveBuildingWing({
      buildingWing: coupon.buildingWing,
      unitLabel: coupon.unitLabel,
      unitType: coupon.unitType,
    }) ?? '',
    coupon.campaignId,
    coupon.discountType,
    String(coupon.discountValue),
    coupon.usageLimit === null ? 'unlimited' : String(coupon.usageLimit ?? 1),
    String(coupon.usedCount ?? 0),
    String(coupon.totalDiscountGiven ?? 0),
    String(coupon.startDate ?? ''),
    String(coupon.endDate ?? ''),
    String(coupon.expiresAt ?? coupon.endDate ?? ''),
    coupon.status,
  ]);
  return [header, ...rows]
    .map((row) => row.map((value) => escapeCsv(String(value ?? ''))).join(','))
    .join('\n');
}

export function couponsToJSON(coupons: Coupon[]): string {
  return JSON.stringify(coupons, null, 2);
}

export function getCouponBarcodeValue(code: string, buildingUnit?: boolean): string {
  const prefix = buildingUnit ? 'UNIT-' : 'CPN-';
  return `${prefix}${code.trim().toUpperCase()}`;
}

function renderCode128DataUrl(value: string): string {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, value, {
    format: 'CODE128',
    displayValue: true,
    margin: 0,
    height: 40,
    fontSize: 11,
  });
  return canvas.toDataURL('image/png');
}

function renderCode128Svg(value: string): string {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  JsBarcode(svg, value, {
    format: 'CODE128',
    displayValue: false,
    margin: 0,
    height: 34,
    width: 1.2,
  });
  return svg.outerHTML;
}

export async function exportCouponsPdfWithBarcodes(campaignName: string, coupons: Coupon[]): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 12;
  const cardWidth = pageWidth - marginX * 2;
  const cardHeight = 38;
  let y = 16;

  doc.setFontSize(14);
  doc.text(`${campaignName} Coupons`, marginX, y);
  y += 8;

  for (let idx = 0; idx < coupons.length; idx++) {
    const coupon = coupons[idx];
    if (y + cardHeight > pageHeight - 10) {
      doc.addPage();
      y = 16;
    }
    const code = coupon.code.trim().toUpperCase();
    const barcodeValue = getCouponBarcodeValue(code);
    const barcodeDataUrl = renderCode128DataUrl(barcodeValue);
    const expiry = coupon.expiresAt ?? coupon.endDate;

    doc.setDrawColor(214, 214, 214);
    doc.rect(marginX, y, cardWidth, cardHeight);
    doc.setFontSize(12);
    doc.text(`Code: ${code}`, marginX + 3, y + 6);
    doc.setFontSize(10);
    doc.text(
      `Discount: ${coupon.discountType === 'percentage' ? `${coupon.discountValue}%` : `$${coupon.discountValue.toFixed(2)}`}`,
      marginX + 3,
      y + 12
    );
    doc.text(`Usage: ${coupon.usedCount ?? 0}/${coupon.usageLimit ?? 1}`, marginX + 3, y + 17);
    doc.text(`Expires: ${expiry ? new Date(expiry).toLocaleDateString('en-NZ') : 'No expiry'}`, marginX + 3, y + 22);
    doc.addImage(barcodeDataUrl, 'PNG', marginX + 3, y + 24, cardWidth - 6, 12);
    y += cardHeight + 4;
  }

  const safeName = campaignName.trim().toLowerCase().replace(/\s+/g, '_') || 'campaign';
  doc.save(`${safeName}_coupons_barcodes.pdf`);
}

type PdfImageFormat = 'JPEG' | 'PNG' | 'WEBP';

interface LoadedPdfImage {
  dataUrl: string;
  width: number;
  height: number;
  format: PdfImageFormat;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read image blob'));
    reader.readAsDataURL(blob);
  });
}

function readImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.src = dataUrl;
  });
}

function mimeToPdfImageFormat(mime: string): PdfImageFormat {
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'JPEG';
  if (mime.includes('webp')) return 'WEBP';
  return 'PNG';
}

async function loadRemoteImage(url: string, cachedDataUrl?: string | null): Promise<LoadedPdfImage> {
  let blob: Blob;
  try {
    blob = await fetchCouponTemplateImageBlob(url, cachedDataUrl);
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'Unknown error';
    throw new Error(`Could not load template image for PDF export. ${detail}`);
  }
  const dataUrl = await blobToDataUrl(blob);
  const { width, height } = await readImageDimensions(dataUrl);
  return {
    dataUrl,
    width,
    height,
    format: mimeToPdfImageFormat(blob.type),
  };
}

function hexToRgb(hex: string): [number, number, number] {
  const raw = hex.replace('#', '').trim();
  const expanded =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw.length >= 6
        ? raw.slice(0, 6)
        : '000000';
  const n = Number.parseInt(expanded, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Map overlay rem sizes to pt — calibrated to match the HTML template editor (~320px card width). */
function overlayRemToPt(fontRem: number, cardWidthMm: number): number {
  const mm = fontRem * (cardWidthMm / 20);
  return (mm * 72) / 25.4;
}

function drawImageCover(
  doc: jsPDF,
  image: LoadedPdfImage,
  x: number,
  y: number,
  boxW: number,
  boxH: number
): void {
  const imgAspect = image.width / image.height;
  const boxAspect = boxW / boxH;
  let drawW = boxW;
  let drawH = boxH;
  let drawX = x;
  let drawY = y;
  if (imgAspect > boxAspect) {
    drawH = boxH;
    drawW = boxH * imgAspect;
    drawX = x - (drawW - boxW) / 2;
  } else {
    drawW = boxW;
    drawH = boxW / imgAspect;
    drawY = y - (drawH - boxH) / 2;
  }
  doc.addImage(image.dataUrl, image.format, drawX, drawY, drawW, drawH, undefined, 'FAST');
}

function drawCenteredOverlayText(
  doc: jsPDF,
  text: string,
  cx: number,
  cy: number,
  fontPt: number,
  color: string,
  options?: { bold?: boolean; maxWidthMm?: number; angle?: number }
): void {
  const [r, g, b] = hexToRgb(color);
  doc.setTextColor(r, g, b);
  doc.setFont('helvetica', options?.bold ? 'bold' : 'normal');
  doc.setFontSize(fontPt);
  const angle = options?.angle ?? 0;
  if (options?.maxWidthMm && options.maxWidthMm > 0) {
    const paragraphs = text.split('\n');
    const lines: string[] = [];
    for (const paragraph of paragraphs) {
      if (paragraph.length === 0) {
        lines.push('');
        continue;
      }
      lines.push(...doc.splitTextToSize(paragraph, options.maxWidthMm));
    }
    doc.text(lines, cx, cy, { align: 'center', baseline: 'middle', angle });
  } else {
    doc.text(text, cx, cy, { align: 'center', baseline: 'middle', angle });
  }
}

function drawCouponOnPdf(
  doc: jsPDF,
  coupon: Coupon,
  cardX: number,
  cardY: number,
  cardW: number,
  cardH: number,
  overlay: CouponPrintOverlayLayout,
  ink: string,
  templateImage: LoadedPdfImage | null,
  conditionsText: string,
  buildingUnit: boolean
): void {
  if (templateImage) {
    drawImageCover(doc, templateImage, cardX, cardY, cardW, cardH);
  } else {
    doc.setDrawColor(216, 216, 216);
    doc.setFillColor(255, 255, 255);
    doc.rect(cardX, cardY, cardW, cardH, 'FD');
  }

  const isUnitCard = buildingUnit || coupon.isPermanent;
  const off =
    coupon.discountType === 'percentage'
      ? `${coupon.discountValue}% OFF`
      : `$${Number(coupon.discountValue).toFixed(2)} OFF`;
  const expiryText = isUnitCard
    ? 'Permanent resident discount'
    : `Expiry: ${formatCouponExpiry(coupon.endDate)}`;

  const placeText = (
    value: string,
    leftPct: number,
    topPct: number,
    fontRem: number,
    bold: boolean,
    maxWidthPct?: number,
    angle?: number
  ) => {
    const cx = cardX + (cardW * leftPct) / 100;
    const cy = cardY + (cardH * topPct) / 100;
    drawCenteredOverlayText(doc, value, cx, cy, overlayRemToPt(fontRem, cardW), ink, {
      bold,
      maxWidthMm: maxWidthPct ? (cardW * maxWidthPct) / 100 : undefined,
      angle,
    });
  };

  if (
    isPrintOverlayVisible(overlay, 'unitLabel') &&
    coupon.unitLabel &&
    coupon.unitLabel.trim().length > 0
  ) {
    placeText(
      coupon.unitLabel.trim(),
      overlay.unitLabelLeftPct ?? 50,
      overlay.unitLabelTopPct ?? 42,
      overlay.unitLabelFontRem ?? 1.35,
      true
    );
  }
  if (isPrintOverlayVisible(overlay, 'code')) {
    placeText(coupon.code, overlay.codeLeftPct, overlay.codeTopPct, overlay.codeFontRem ?? 0.95, true);
  }
  if (isPrintOverlayVisible(overlay, 'discount')) {
    placeText(off, overlay.offLeftPct, overlay.offTopPct, overlay.offFontRem ?? 1.72, true);
  }
  if (isPrintOverlayVisible(overlay, 'expiry')) {
    placeText(
      expiryText,
      overlay.expiryLeftPct,
      overlay.expiryTopPct,
      overlay.expiryFontRem ?? 0.68,
      false
    );
  }
  if (
    isPrintOverlayVisible(overlay, 'conditions') &&
    conditionsText.length > 0
  ) {
    placeText(
      conditionsText,
      overlay.conditionsLeftPct ?? 50,
      overlay.conditionsTopPct ?? 92,
      overlay.conditionsFontRem ?? 0.48,
      false,
      overlay.conditionsWidthPct ?? 88,
      overlay.conditionsRotateDeg ?? 0
    );
  }
  if (isPrintOverlayVisible(overlay, 'barcode')) {
    const barcodeValue = getCouponBarcodeValue(coupon.code, isUnitCard);
    const barcodeDataUrl = renderCode128DataUrl(barcodeValue);
    const cx = cardX + (cardW * (overlay.barcodeLeftPct ?? 50)) / 100;
    const cy = cardY + (cardH * (overlay.barcodeTopPct ?? 88)) / 100;
    const bw = (cardW * (overlay.barcodeWidthPct ?? 62)) / 100;
    const bh = (cardH * (overlay.barcodeHeightPct ?? 12)) / 100;
    const rot = overlay.barcodeRotateDeg ?? 0;
    doc.addImage(
      barcodeDataUrl,
      'PNG',
      cx - bw / 2,
      cy - bh / 2,
      bw,
      bh,
      undefined,
      'FAST',
      rot
    );
  }
}

export interface CouponA4PdfExportOptions extends CouponPrintBranding {
  onProgress?: (completed: number, total: number) => void;
}

/**
 * Build an A4 PDF with the same mm grid + template overlays as the print preview, then save locally.
 * Avoids the browser print dialog (Chrome preview hangs on large sheets).
 */
export async function exportCouponsA4SheetPdf(
  campaignName: string,
  coupons: Coupon[],
  options?: CouponA4PdfExportOptions
): Promise<void> {
  if (coupons.length === 0) {
    throw new Error('No coupons to export.');
  }

  const buildingUnit = options?.buildingUnit ?? coupons.some((c) => c.isPermanent);
  const cardSize = options?.cardAspectRatio ?? (buildingUnit ? '86/54' : '85.6/54');
  const templateUrl = options?.templateImageUrl?.trim() ?? '';
  const hasTemplate = templateUrl.length > 0;
  const mmSheetConfig = isMmSheetCardSize(cardSize) ? getMmSheetLayoutConfig(cardSize) : null;
  const useMmSheetLayout = Boolean(mmSheetConfig && hasTemplate);
  const sheetRowsMm =
    useMmSheetLayout && mmSheetConfig
      ? clampA4Rows(mmSheetConfig, options?.a4SheetRows95x38 ?? mmSheetConfig.defaultRows)
      : mmSheetConfig?.defaultRows ?? 4;
  const overlay = mergeCouponPrintOverlay(
    options?.overlayLayout ?? undefined,
    buildingUnit ? 'building_unit' : 'promotional'
  );
  const ink = sanitizeCssColor(overlay.textColor, '#ffffff');
  const conditionsText = (options?.conditionsText ?? '').trim();

  const templateImage = hasTemplate
    ? await loadRemoteImage(templateUrl, options?.templateImageDataUrl)
    : null;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = useMmSheetLayout ? COUPON_A4_MM_PAGE_MARGIN_MM : 8;

  let couponsPerPage = 2;
  let gridW = pageWidth - margin * 2;
  let gridH = 0;
  let cardW = (pageWidth - margin * 2 - 2) / 2;
  let cardH = 38;
  let colGap = 2;
  let rowGap = 2;
  let cols = 2;
  let rows = 1;

  if (useMmSheetLayout && mmSheetConfig) {
    cols = mmSheetConfig.cols;
    rows = sheetRowsMm;
    cardW = mmSheetConfig.cardWidthMm;
    cardH = mmSheetConfig.cardHeightMm;
    colGap = mmSheetConfig.colGapMm;
    rowGap = mmSheetConfig.rowGapMm;
    gridW = gridWidthMm(mmSheetConfig);
    gridH = gridHeightMm(mmSheetConfig, sheetRowsMm);
    couponsPerPage = couponsPerSheet(mmSheetConfig, sheetRowsMm);
  }

  const gridX = (pageWidth - gridW) / 2;
  const gridY = margin;
  const total = coupons.length;

  for (let index = 0; index < coupons.length; index++) {
    const slotOnPage = index % couponsPerPage;
    if (index > 0 && slotOnPage === 0) {
      doc.addPage();
    }

    const col = slotOnPage % cols;
    const row = Math.floor(slotOnPage / cols);
    const cardX = gridX + col * (cardW + colGap);
    const cardY = gridY + row * (cardH + rowGap);

    drawCouponOnPdf(
      doc,
      coupons[index],
      cardX,
      cardY,
      cardW,
      cardH,
      overlay,
      ink,
      templateImage,
      conditionsText,
      buildingUnit
    );

    options?.onProgress?.(index + 1, total);

    if (useMmSheetLayout && mmSheetConfig && row === rows - 1 && col === cols - 1) {
      const usedHeight = gridY + gridH;
      if (usedHeight > pageHeight - margin) {
        console.warn('Coupon sheet grid may exceed printable A4 height', { usedHeight, pageHeight });
      }
    }
  }

  const safeName = campaignName.trim().toLowerCase().replace(/\s+/g, '_') || 'campaign';
  doc.save(`${safeName}_coupons_a4.pdf`);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatCouponExpiry(timestamp?: number | null): string {
  if (!timestamp) return 'No expiry';
  return new Date(timestamp).toLocaleDateString('en-NZ');
}

/** Optional branding for A4 coupon print (matches POS pink / dark theme). */
export interface CouponPrintBranding {
  /** Header wordmark; default `KINGS PARK`. */
  venueName?: string;
  /** Small line under venue name; default `Restaurant`. */
  tagline?: string;
  /**
   * Absolute URL to a logo image (PNG/SVG). Same-origin URLs print most reliably.
   * If omitted, a built-in KP mark is used.
   */
  logoUrl?: string | null;
  /**
   * Optional full coupon card image (light-theme template). If provided, only the code/discount/expiry
   * text is overlaid on top so output can be pixel-identical to the supplied design.
   */
  templateImageUrl?: string | null;
  /** Overlay positions / colors (from campaign or editor draft). */
  overlayLayout?: Partial<CouponPrintOverlayLayout> | null;
  /** Template card size used for print layout. */
  cardAspectRatio?: CouponCardSize;
  /** Rows on each A4 when using a fixed mm card size (see `printA4SheetRows95x38` on campaign). */
  a4SheetRows95x38?: number;
  /** Terms / conditions lines (from campaign); shown when non-empty. */
  conditionsText?: string | null;
  /** Building unit cards use UNIT- barcodes and show room/apt label on print. */
  buildingUnit?: boolean;
  /** JPEG/PNG data URL cached on campaign — used for PDF when Storage CORS blocks the browser. */
  templateImageDataUrl?: string | null;
}

/** Inline SVG mark when no `logoUrl` — prints without external assets. */
function defaultBrandMarkSvg(): string {
  return `<svg class="brand-mark-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 56" width="52" height="52" role="img" aria-label="Kings Park">
  <circle cx="28" cy="28" r="26" fill="#f8fafc" stroke="#d1d5db" stroke-width="1.5"/>
  <text x="28" y="35" text-anchor="middle" fill="#0f4c81" font-family="system-ui, -apple-system, Segoe UI, sans-serif" font-weight="800" font-size="17">KP</text>
</svg>`;
}

function brandLogoHtml(logoUrl?: string | null): string {
  const trimmed = logoUrl?.trim();
  if (trimmed) {
    const safe = escapeHtml(trimmed);
    return `<img class="brand-img" src="${safe}" alt="" width="140" height="56" />`;
  }
  return defaultBrandMarkSvg();
}

/**
 * Full HTML document for A4 coupon sheets. Self-contained (no Tailwind) so it works in a print popup.
 * Uses Kings Park POS colours (pink gradient header, rose accents) aligned with the main app.
 */
export function buildCouponA4PrintDocumentHtml(
  campaignName: string,
  coupons: Coupon[],
  branding?: CouponPrintBranding
): string {
  const venue = escapeHtml(branding?.venueName?.trim() || 'KINGS PARK');
  const logoBlock = brandLogoHtml(branding?.logoUrl ?? undefined);
  const templateImageUrl = branding?.templateImageUrl?.trim()
    ? escapeHtml(branding.templateImageUrl.trim())
    : '';
  const hasTemplateCardImage = templateImageUrl.length > 0;
  const buildingUnit = branding?.buildingUnit ?? coupons.some((c) => c.isPermanent);
  const cardSize =
    branding?.cardAspectRatio ?? (buildingUnit ? '86/54' : '85.6/54');
  const cardAspectRatio = cssAspectRatioForCardSize(cardSize);
  const mmSheetConfig = isMmSheetCardSize(cardSize) ? getMmSheetLayoutConfig(cardSize) : null;
  /** Fixed mm grid only when template cards are used; built-in cards need flowing layout. */
  const useMmSheetLayout = Boolean(mmSheetConfig && hasTemplateCardImage);
  const sheetRowsMm =
    useMmSheetLayout && mmSheetConfig
      ? clampA4Rows(mmSheetConfig, branding?.a4SheetRows95x38 ?? mmSheetConfig.defaultRows)
      : mmSheetConfig?.defaultRows ?? 1;
  const overlay = mergeCouponPrintOverlay(
    branding?.overlayLayout ?? undefined,
    buildingUnit ? 'building_unit' : 'promotional'
  );
  const ink = sanitizeCssColor(overlay.textColor, '#ffffff');
  const overlayBase = `transform:translate(-50%,-50%);color:${ink};text-shadow:none;text-align:center;white-space:nowrap;`;
  const styleCode = `left:${overlay.codeLeftPct}%;top:${overlay.codeTopPct}%;${overlayBase}font-size:${overlay.codeFontRem}rem;font-weight:800;letter-spacing:0.04em;`;
  const styleOff = `left:${overlay.offLeftPct}%;top:${overlay.offTopPct}%;${overlayBase}font-size:${overlay.offFontRem}rem;font-weight:900;letter-spacing:0;`;
  const styleExpiry = `left:${overlay.expiryLeftPct}%;top:${overlay.expiryTopPct}%;${overlayBase}font-size:${overlay.expiryFontRem}rem;font-weight:500;letter-spacing:0.02em;`;
  const styleUnitLabel = `left:${overlay.unitLabelLeftPct ?? 50}%;top:${overlay.unitLabelTopPct ?? 42}%;${overlayBase}font-size:${overlay.unitLabelFontRem ?? 1.35}rem;font-weight:900;letter-spacing:0.02em;`;
  const rot = overlay.barcodeRotateDeg ?? 0;
  const styleBarcodeSlot = `left:${overlay.barcodeLeftPct}%;top:${overlay.barcodeTopPct}%;width:${overlay.barcodeWidthPct}%;height:${overlay.barcodeHeightPct}%;transform:translate(-50%,-50%) rotate(${rot}deg);transform-origin:center center;`;

  const conditionsPlain = (branding?.conditionsText ?? '').trim();
  const conditionsBody = conditionsPlain.length > 0 ? escapeHtml(conditionsPlain) : '';
  const cRot = overlay.conditionsRotateDeg ?? 0;
  const cFont = overlay.conditionsFontRem ?? 0.48;
  const cW = overlay.conditionsWidthPct ?? 88;
  const conditionsBox = `left:${overlay.conditionsLeftPct ?? 50}%;top:${overlay.conditionsTopPct ?? 92}%;width:${cW}%;min-width:0;max-width:${cW}%;transform:translate(-50%,-50%) rotate(${cRot}deg);transform-origin:center center;text-align:center;white-space:pre-wrap;word-wrap:break-word;line-height:1.25;font-size:${cFont}rem;font-weight:500;box-sizing:border-box;padding:0 2px;`;
  const styleConditionsTemplate =
    conditionsBody.length > 0 ? `${conditionsBox}color:${ink};text-shadow:none;` : '';
  const styleConditionsPlain =
    conditionsBody.length > 0 ? `${conditionsBox}color:#374151;text-shadow:none;` : '';

  const renderCouponCard = (coupon: Coupon) => {
    const off =
      coupon.discountType === 'percentage'
        ? `${coupon.discountValue}% OFF`
        : `$${Number(coupon.discountValue).toFixed(2)} OFF`;
    const isUnitCard = buildingUnit || coupon.isPermanent;
    const barcodeValue = getCouponBarcodeValue(coupon.code, isUnitCard);
    const unitLabelBlock =
      isPrintOverlayVisible(overlay, 'unitLabel') &&
      coupon.unitLabel &&
      coupon.unitLabel.trim().length > 0
        ? `<div class="template-overlay template-overlay--unit" style="${styleUnitLabel}">${escapeHtml(coupon.unitLabel.trim())}</div>`
        : '';
    const expiryText = isUnitCard
      ? 'Permanent resident discount'
      : `Expiry: ${escapeHtml(formatCouponExpiry(coupon.endDate))}`;
    if (hasTemplateCardImage) {
      const barcodeImg = renderCode128DataUrl(barcodeValue);
      const mmClass = useMmSheetLayout ? ' coupon-card--mm-fixed' : '';
      return `
        <div class="coupon-card coupon-card--template${mmClass}">
          <img class="template-base-image" src="${templateImageUrl}" alt="" decoding="async" loading="eager" />
          ${unitLabelBlock}
          ${isPrintOverlayVisible(overlay, 'code') ? `<div class="template-overlay template-overlay--code" style="${styleCode}">${escapeHtml(coupon.code)}</div>` : ''}
          ${isPrintOverlayVisible(overlay, 'discount') ? `<div class="template-overlay template-overlay--off" style="${styleOff}">${escapeHtml(off)}</div>` : ''}
          ${isPrintOverlayVisible(overlay, 'expiry') ? `<div class="template-overlay template-overlay--expiry" style="${styleExpiry}">${expiryText}</div>` : ''}
          ${isPrintOverlayVisible(overlay, 'conditions') && conditionsBody.length > 0 ? `<div class="template-overlay template-overlay--conditions" style="${styleConditionsTemplate}">${conditionsBody}</div>` : ''}
          ${isPrintOverlayVisible(overlay, 'barcode') ? `<div class="template-barcode-slot" style="${styleBarcodeSlot}"><img class="template-barcode-img" src="${barcodeImg}" alt="" decoding="async" /></div>` : ''}
        </div>`;
    }
    const barcodeSvg = renderCode128Svg(barcodeValue);
    const unitPlain =
      isPrintOverlayVisible(overlay, 'unitLabel') &&
      coupon.unitLabel &&
      coupon.unitLabel.trim().length > 0
        ? `<div class="unit-label-plain">${escapeHtml(coupon.unitLabel.trim())}</div>`
        : '';
    return `
        <div class="coupon-card">
          <div class="coupon-inner">
            <div class="coupon-logo-wrap">${logoBlock}</div>
            ${unitPlain}
            ${isPrintOverlayVisible(overlay, 'code') ? `<div class="label">${isUnitCard ? 'BUILDING CARD' : 'COUPON CODE'}</div><div class="code">${escapeHtml(coupon.code)}</div>` : ''}
            ${isPrintOverlayVisible(overlay, 'discount') ? `<div class="off">${escapeHtml(off)}</div>` : ''}
            ${isPrintOverlayVisible(overlay, 'expiry') ? `<div class="expiry">${expiryText}</div>` : ''}
            ${isPrintOverlayVisible(overlay, 'barcode') ? `<div class="barcode-slot-plain">${barcodeSvg}</div>` : ''}
            ${isPrintOverlayVisible(overlay, 'conditions') && conditionsBody.length > 0 ? `<div class="template-overlay template-overlay--conditions coupon-conditions-on-plain" style="${styleConditionsPlain}">${conditionsBody}</div>` : ''}
          </div>
        </div>`;
  };

  const mmGridW = mmSheetConfig ? gridWidthMm(mmSheetConfig) : 0;
  const mmGridH = mmSheetConfig ? gridHeightMm(mmSheetConfig, sheetRowsMm) : 0;
  const mmCutX = mmSheetConfig ? verticalCutXMm(mmSheetConfig) : 0;
  const couponsPerMmSheet =
    useMmSheetLayout && mmSheetConfig ? couponsPerSheet(mmSheetConfig, sheetRowsMm) : 10;
  const mmHorizontalCutYs = mmSheetConfig ? horizontalCutYsMm(mmSheetConfig, sheetRowsMm) : [];
  const mmCutSvg =
    useMmSheetLayout && mmSheetConfig
      ? `<svg class="sheet-cut-svg" xmlns="http://www.w3.org/2000/svg" width="${mmGridW}mm" height="${mmGridH}mm" viewBox="0 0 ${mmGridW} ${mmGridH}" preserveAspectRatio="none" aria-hidden="true">
  ${mmHorizontalCutYs
    .map(
      (y) =>
        `<line x1="0" y1="${y}" x2="${mmGridW}" y2="${y}" stroke="#111827" stroke-width="0.25" stroke-dasharray="2.5 2" opacity="0.75"/>`
    )
    .join('')}
  <line x1="${mmCutX}" y1="0" x2="${mmCutX}" y2="${mmGridH}" stroke="#111827" stroke-width="0.25" stroke-dasharray="2.5 2" opacity="0.75"/>
</svg>`
      : '';

  const sheetsHtml = (() => {
    if (useMmSheetLayout && mmSheetConfig) {
      const chunks = [];
      for (let i = 0; i < coupons.length; i += couponsPerMmSheet) {
        chunks.push(coupons.slice(i, i + couponsPerMmSheet));
      }
      if (chunks.length === 0) {
        return `<div class="sheet"><div class="sheet-mm-wrap">${mmCutSvg}<div class="grid grid--a4-mm"></div></div></div>`;
      }
      return chunks
        .map(
          (chunk) =>
            `<div class="sheet"><div class="sheet-mm-wrap">${mmCutSvg}<div class="grid grid--a4-mm">${chunk.map((c) => renderCouponCard(c)).join('')}</div></div></div>`
        )
        .join('');
    }
    return `<div class="sheet"><div class="grid">${coupons.map((c) => renderCouponCard(c)).join('')}</div></div>`;
  })();

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Print — ${venue}</title>
  <style>
    @page { size: A4; margin: ${useMmSheetLayout ? `${COUPON_A4_MM_PAGE_MARGIN_MM}mm` : '8mm'}; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 0 0 6px;
      font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
      color: #111827;
      background: #ececec;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .brand-mark-svg {
      display: block;
      margin: 0 auto;
    }
    .brand-img {
      display: block;
      margin: 0 auto;
      max-height: 110px;
      width: 92%;
      object-fit: contain;
    }
    .sheet {
      page-break-after: always;
    }
    .sheet:last-child {
      page-break-after: auto;
    }
    .grid {
      max-width: 100%;
      margin: 0 auto;
      padding: 0;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: ${useMmSheetLayout ? '1.5mm' : '2mm'};
    }
    .sheet-mm-wrap {
      position: relative;
      width: ${mmGridW}mm;
      margin: 0 auto 3mm;
      box-sizing: border-box;
      border: 0.3mm dashed rgba(17, 24, 39, 0.55);
    }
    .sheet-cut-svg {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 0;
    }
    .grid.grid--a4-mm {
      position: relative;
      z-index: 1;
      width: 100%;
      max-width: ${mmGridW}mm;
      margin: 0 auto;
      grid-template-columns: repeat(${mmSheetConfig?.cols ?? 2}, ${mmSheetConfig?.cardWidthMm ?? 95}mm);
      grid-template-rows: repeat(${sheetRowsMm}, ${mmSheetConfig?.cardHeightMm ?? 38}mm);
      column-gap: ${mmSheetConfig?.colGapMm ?? 10}mm;
      row-gap: ${mmSheetConfig?.rowGapMm ?? 2}mm;
      justify-content: center;
      align-content: start;
    }
    .coupon-card {
      position: relative;
      background: #ffffff;
      border: 1px solid #d8d8d8;
      border-radius: 14px;
      padding: 2.4mm;
      min-height: 50mm;
      break-inside: avoid;
      page-break-inside: avoid;
      box-shadow: 0 3px 16px rgba(0, 0, 0, 0.08);
    }
    .coupon-card--template {
      border: none;
      padding: 0;
      border-radius: 0;
      box-shadow: none;
      background-color: #ffffff;
      width: 100%;
      aspect-ratio: ${cardAspectRatio};
      height: auto;
      min-height: 0;
      break-inside: avoid;
      page-break-inside: avoid;
      overflow: hidden;
    }
    .template-base-image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      z-index: 0;
      display: block;
      pointer-events: none;
    }
    .coupon-card--template.coupon-card--mm-fixed {
      width: ${mmSheetConfig?.cardWidthMm ?? 95}mm;
      height: ${mmSheetConfig?.cardHeightMm ?? 38}mm;
      max-height: ${mmSheetConfig?.cardHeightMm ?? 38}mm;
      min-height: 0 !important;
      aspect-ratio: unset;
      outline: 0.22mm dashed rgba(17, 24, 39, 0.65);
      outline-offset: -0.35mm;
    }
    .template-overlay {
      position: absolute;
      line-height: 1.1;
      font-family: "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      text-rendering: geometricPrecision;
      text-shadow: none;
      z-index: 2;
      width: 88%;
      max-width: 88%;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .template-overlay--conditions {
      z-index: 5;
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow: visible;
      text-overflow: unset;
      width: auto;
      max-width: none;
    }
    .coupon-conditions-on-plain {
      z-index: 3;
    }
    .template-barcode-slot {
      position: absolute;
      z-index: 3;
      padding: 0;
      border: none;
      background: transparent;
      overflow: visible;
      box-sizing: border-box;
    }
    .template-barcode-slot svg,
    .template-barcode-slot .template-barcode-img {
      display: block;
      width: 100%;
      height: 100%;
      min-height: 0;
      object-fit: contain;
    }
    .coupon-inner {
      border: 1px solid #e6e6e6;
      border-radius: 10px;
      padding: 2.4mm 3mm 2.6mm;
      min-height: 44mm;
    }
    .coupon-logo-wrap {
      min-height: 118px;
      margin: 0 0 10px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .label {
      text-align: center;
      font-size: 0.64rem;
      color: #374151;
      text-transform: uppercase;
      letter-spacing: 0.14em;
      font-weight: 500;
    }
    .code {
      text-align: center;
      font-size: 1.18rem;
      font-weight: 800;
      letter-spacing: 0.045em;
      margin: 8px 0 9px;
      font-family: "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      color: #111827;
    }
    .off {
      text-align: center;
      font-size: 3.05rem;
      line-height: 1;
      font-weight: 900;
      color: #111827;
      margin-top: 2px;
    }
    .expiry {
      text-align: center;
      font-size: 0.82rem;
      color: #374151;
      margin-top: 10px;
    }
    .barcode-slot-plain {
      margin-top: 8px;
      padding: 0;
      border: none;
      background: transparent;
    }
    .barcode-slot-plain svg {
      display: block;
      width: 100%;
      height: 34px;
    }
    .sheet-footer {
      display: none;
    }
    .print-toolbar {
      position: sticky;
      top: 0;
      z-index: 100;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 12px;
      padding: 10px 14px;
      background: #111827;
      color: #f9fafb;
      font-size: 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    }
    .print-toolbar button {
      padding: 8px 16px;
      border: none;
      border-radius: 6px;
      background: #ec4899;
      color: #fff;
      font-weight: 600;
      font-size: 14px;
      cursor: pointer;
    }
    .print-toolbar button:hover:not(:disabled) {
      background: #db2777;
    }
    .print-toolbar button:disabled {
      opacity: 0.55;
      cursor: wait;
    }
    .print-toolbar-hint {
      font-size: 12px;
      color: #d1d5db;
      max-width: 520px;
      line-height: 1.4;
    }
    @media print {
      body { background: #fff; }
      .print-toolbar { display: none !important; }
      .coupon-card {
        box-shadow: none;
        border-color: #d4d4d8;
      }
      .coupon-inner {
        border-color: #dfdfdf;
      }
      .coupon-card--template,
      .template-base-image {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .sheet-cut-svg {
        display: none !important;
      }
    }
  </style>
</head>
<body>
  <div class="print-toolbar">
    <button id="print-btn" type="button" disabled>Preparing…</button>
    <p id="print-status" class="print-toolbar-hint">Loading card images — do not open Print until this button is enabled.</p>
  </div>
  ${sheetsHtml}
  <script>
    (function () {
      var btn = document.getElementById('print-btn');
      var status = document.getElementById('print-status');
      if (!btn) return;
      function markReady(note) {
        btn.disabled = false;
        btn.textContent = 'Print / Save as PDF';
        if (status) status.textContent = note || 'Ready — scroll to confirm cards look correct, then click Print. Choose Save as PDF. Background graphics can stay off.';
      }
      function imagesReady() {
        var imgs = document.querySelectorAll('img.template-base-image, img.template-barcode-img');
        if (!imgs.length) return true;
        for (var i = 0; i < imgs.length; i++) {
          if (!imgs[i].complete || imgs[i].naturalWidth === 0) return false;
        }
        return true;
      }
      function watchImages() {
        if (imagesReady()) {
          markReady();
          return;
        }
        var imgs = document.querySelectorAll('img.template-base-image, img.template-barcode-img');
        var pending = 0;
        var finished = false;
        var tryFinish = function () {
          if (finished) return;
          if (imagesReady()) {
            finished = true;
            markReady();
          }
        };
        for (var i = 0; i < imgs.length; i++) {
          if (imgs[i].complete && imgs[i].naturalWidth > 0) continue;
          pending++;
          imgs[i].addEventListener('load', tryFinish, { once: true });
          imgs[i].addEventListener('error', tryFinish, { once: true });
        }
        if (pending === 0) tryFinish();
        setTimeout(function () {
          markReady('Ready — if any card looks blank, wait a few seconds and click Print again.');
        }, 25000);
      }
      btn.addEventListener('click', function () {
        try { window.focus(); } catch (e) {}
        window.print();
      });
      if (document.readyState === 'complete') watchImages();
      else window.addEventListener('load', watchImages);
    })();
  </script>
</body>
</html>`;
}

